import { UserRole, UserRoleInfo, UserInfo } from '../types/index';
import { globalStore } from './globalStore';

/**
 * 用户角色管理工具类
 */
export class UserRoleManager {
  /**
   * 根据用户名判断用户角色（备用方法，优先使用API返回的角色信息）
   * @param username 用户名
   * @returns 用户角色信息
   */
  static getUserRoleByUsername(username: string): UserRoleInfo {
    const isAdmin = username.toLowerCase() === 'admin';
    const role = isAdmin ? UserRole.ADMINISTRATOR : UserRole.OPERATOR;
    
    return {
      role,
      isAdmin,
      isAssessor: false,
      isOperator: !isAdmin,
      isUser: false,
      permissions: this.getPermissionsByRole(role)
    };
  }

  /**
   * 根据用户信息判断用户角色
   * @param userInfo 用户信息
   * @returns 用户角色信息
   */
  static getUserRole(userInfo: UserInfo): UserRoleInfo {
    // 优先使用 roles 数组（来自接口的最新数据），而不是 role 字段（可能是旧数据）
    // 如果用户信息包含 roles，根据 roles 判断角色
    // 兼容两种格式：["super_admin"] 或 [{ role_key: "super_admin", ... }]
    // 优先级：管理员 > 审核员 > 操作者（如果用户有多个角色，优先使用最高权限的角色）
    if (userInfo as any && Array.isArray((userInfo as any).roles) && (userInfo as any).roles.length > 0) {
      const rawRoles: any[] = (userInfo as any).roles;
      
      // 提取所有角色 key（支持多种格式）
      const roleKeys: string[] = rawRoles.map((role: any, index: number) => {
        if (typeof role === 'string') {
          return role.toLowerCase().trim();
        }
        // 支持多种可能的字段名：role_key, code, key, name, role
        const roleKey = (role && (
          role.role_key || 
          role.code || 
          role.key || 
          role.name || 
          role.role ||
          ''
        )) || '';
        const normalizedKey = roleKey.toLowerCase().trim();
        console.log(`[getUserRole] roles[${index}] 提取:`, {
          raw: role,
          role_key: role?.role_key,
          code: role?.code,
          key: role?.key,
          name: role?.name,
          role: role?.role,
          extracted: normalizedKey
        });
        return normalizedKey;
      }).filter((key: string) => key); // 过滤空值
      
      // 按优先级查找角色：管理员 > 审核员 > 操作者 > 普通用户
      // 注意：roleKeys 已经是小写，所以比较时也要用小写
      let roleKey = '';
      if (roleKeys.some(key => key === 'super_admin' || key === 'admin' || key === 'administrator')) {
        // 优先判断管理员（优先 super_admin）
        roleKey = roleKeys.find(key => key === 'super_admin') || 
                  roleKeys.find(key => key === 'admin') || 
                  roleKeys.find(key => key === 'administrator') || 
                  '';
      } else if (roleKeys.some(key => key === 'reviewer' || key === 'assessor')) {
        // 其次判断审核员
        roleKey = roleKeys.find(key => key === 'reviewer' || key === 'assessor') || '';
      } else if (roleKeys.some(key => key === 'operator')) {
        // 再次判断操作者
        roleKey = 'operator';
      } else if (roleKeys.some(key => key === 'user')) {
        // 普通用户（权限最低）
        roleKey = 'user';
      } else if (roleKeys.length > 0) {
        // 如果都不匹配，使用第一个角色
        roleKey = roleKeys[0];
      } else {
        // 默认操作者
        roleKey = 'operator';
      }

      const role = this.getRoleByKey(roleKey);

      return {
        role,
        isAdmin: role === UserRole.ADMINISTRATOR,
        isAssessor: role === UserRole.ASSESSOR,
        isOperator: role === UserRole.OPERATOR,
        isUser: role === UserRole.USER,
        // 如果后端已经下发了完整 permissions，就直接用；否则按角色填默认权限
        permissions: (userInfo as any).permissions || this.getPermissionsByRole(role),
      };
    }

    // 如果没有 roles 数组，回退到使用 role 字段（兼容旧数据格式）
    if (userInfo.role) {
      return {
        role: userInfo.role,
        isAdmin: userInfo.role === UserRole.ADMINISTRATOR,
        isAssessor: userInfo.role === UserRole.ASSESSOR,
        isOperator: userInfo.role === UserRole.OPERATOR,
        isUser: userInfo.role === UserRole.USER,
        permissions: userInfo.permissions || this.getPermissionsByRole(userInfo.role)
      };
    }

    // 最后根据用户名判断（备用方法）
    return this.getUserRoleByUsername(userInfo.username);
  }

  /**
   * 从localStorage获取当前用户角色
   * @returns 用户角色信息，如果未登录则返回null
   */
  static getCurrentUserRole(): UserRoleInfo | null {
    try {
      // 优先从 globalStore 获取（确保使用最新的角色信息）
      const globalUserInfo = globalStore.getUserInfo();
      if (globalUserInfo) {
        return this.getUserRole(globalUserInfo);
      }
      
      // 如果 globalStore 中没有，回退到 localStorage 的 userInfo
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        const userInfo: UserInfo = JSON.parse(userInfoStr);
        return this.getUserRole(userInfo);
      }
      
      return null;
    } catch (error) {
      console.error('获取用户角色信息失败:', error);
      return null;
    }
  }

  /**
   * 检查当前用户是否为管理员
   * @returns 是否为管理员
   */
  static isCurrentUserAdmin(): boolean {
    const roleInfo = this.getCurrentUserRole();
    return roleInfo?.isAdmin || false;
  }

  /**
   * 检查当前用户是否具有指定权限
   * @param permission 权限名称
   * @returns 是否具有权限
   */
  static hasPermission(permission: string): boolean {
    const roleInfo = this.getCurrentUserRole();
    if (!roleInfo) {
      return false;
    }
    
    return roleInfo.permissions.includes(permission);
  }

  /**
   * 根据角色key获取UserRole枚举
   * @param roleKey 角色key
   * @returns UserRole枚举值
   */
  private static getRoleByKey(roleKey: string): UserRole {
    switch (roleKey) {
      case 'operator':
        // 操作者
        return UserRole.OPERATOR;
      case 'user':
        // 普通用户（独立角色）
        return UserRole.USER;
      case 'reviewer':
        // 审核员
        return UserRole.ASSESSOR;
      case 'admin':
      case 'super_admin':
      case 'administrator':
        // 管理员 & 超级管理员（与管理员同等对待）
        return UserRole.ADMINISTRATOR;
      default:
        // 未知角色，默认为操作员
        return UserRole.OPERATOR;
      }
  }

  /**
   * 根据角色获取权限列表
   * @param role 用户角色
   * @returns 权限列表
   */
  private static getPermissionsByRole(role: UserRole): string[] {
    switch (role) {
      case UserRole.ADMINISTRATOR:
        return [
          'user:read',
          'user:write',
          'user:delete',
          'content:read',
          'content:write',
          'content:delete',
          'content:approve',
          'system:settings',
          'system:logs',
          'admin:all',
          'assessor:all'
        ];
      case UserRole.ASSESSOR:
        return [
          'user:read',
          'content:read',
          'content:approve',
          'content:reject',
          'assessor:all'
        ];
      case UserRole.OPERATOR:
        return [
          'user:read',
          'content:read',
          'content:write',
          'operator:all'
        ];
      case UserRole.USER:
        return [
          'user:read',
          'content:read',
          'user:all'
        ];
      default:
        return [];
    }
  }

  /**
   * 更新用户角色信息到localStorage
   * @param userInfo 用户信息
   */
  static updateUserRoleInfo(userInfo: UserInfo): void {
    const roleInfo = this.getUserRole(userInfo);
    const updatedUserInfo = {
      ...userInfo,
      role: roleInfo.role,
      permissions: roleInfo.permissions
    };
    
    localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
  }

  /**
   * 清除用户角色信息
   */
  static clearUserRoleInfo(): void {
    localStorage.removeItem('userInfo');
    localStorage.removeItem('token');
  }
}